import React, { useEffect, useRef, useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import { DragDropContext, Draggable, Droppable } from '@hello-pangea/dnd';
import NewIcon from 'assets/myLeads/new.svg';
import StatusIcon from 'assets/myLeads/status.svg';
import { selectUserInfo } from 'features/auth/authSlice';
import {
  bulkChangeLeadStatus,
  getLeadsKanbanColumn,
  updateLeadPriority,
} from 'features/newsales/newsalesActions';

import LeadCard from '../LeadCard';
import {
  BoardContainer,
  CardsContainer,
  CheckBoxBlock,
  Column,
  ColumnCheckbox,
  ColumnCount,
  ColumnHeader,
  ColumnHeaderLeft,
  ColumnIcon,
  ColumnTitle,
  EmptyMessage,
  IconStatus,
} from './KanbanBoard.styles';

const KanbanBoard = ({
  columns,
  setColumns,
  columnDefinitions = [],
  columnPagination = {},
  selectedLeadIds = [],
  onSelectionChange,
}) => {
  const dispatch = useDispatch();
  const userInfo = useSelector(selectUserInfo);
  const userType = localStorage.getItem('userType');
  const [selectedCards, setSelectedCards] = useState(new Set());
  const [columnPages, setColumnPages] = useState({});
  const [columnHasMore, setColumnHasMore] = useState({});
  const [columnLoading, setColumnLoading] = useState({});

  useEffect(() => {
    const currentSelected = Array.from(selectedCards).sort().join(',');
    const newSelected = [...selectedLeadIds].sort().join(',');

    if (currentSelected !== newSelected) {
      setSelectedCards(new Set(selectedLeadIds));
    }
  }, [selectedLeadIds, selectedCards]);

  useEffect(() => {
    const initialPages = {};
    const initialHasMore = {};
    const initialLoading = {};
    columnDefinitions.forEach((col) => {
      const pagination = columnPagination[col.id];
      initialPages[col.id] = pagination?.page || 1;
      initialHasMore[col.id] = pagination?.hasMore ?? (columns[col.id]?.length || 0) >= 10;
      initialLoading[col.id] = false;
    });
    setColumnPages(initialPages);
    setColumnHasMore(initialHasMore);
    setColumnLoading(initialLoading);
  }, [columnDefinitions, columnPagination]);

  const loadMoreLeads = async (columnId) => {
    if (columnLoading[columnId] || !columnHasMore[columnId]) {
      return;
    }

    setColumnLoading((prev) => ({ ...prev, [columnId]: true }));

    try {
      const nextPage = (columnPages[columnId] || 1) + 1;

      const params = {
        page: nextPage,
        limit: 10,
      };

      const response = await dispatch(
        getLeadsKanbanColumn({
          workflowStatusId: columnId,
          params,
        })
      ).unwrap();

      if (response && response.leads && Array.isArray(response.leads)) {
        if (response.leads.length > 0) {
          const transformedLeads = response.leads.map((lead) => ({
            ...lead,
            id: lead.id.toString(),
            companyName:
              lead.businessType === 'B2B'
                ? lead.companyName
                : `${lead.firstName || ''} ${lead.lastName || ''}`.trim() || 'N/A',
            assigneeUuid: lead.assigneeUuid,
            priority: lead.priority || 'none',
            status: columnId,
            workflowStatusId: columnId,
          }));

          setColumns((prev) => ({
            ...prev,
            [columnId]: [...(prev[columnId] || []), ...transformedLeads],
          }));

          setColumnPages((prev) => ({ ...prev, [columnId]: nextPage }));

          const hasMore = response.pagination?.hasMore ?? false;
          setColumnHasMore((prev) => ({ ...prev, [columnId]: hasMore }));
        } else {
          setColumnHasMore((prev) => ({ ...prev, [columnId]: false }));
        }
      } else {
        setColumnHasMore((prev) => ({ ...prev, [columnId]: false }));
      }
    } catch (error) {
      console.error('Failed to load more leads for column:', columnId, error);
    } finally {
      setColumnLoading((prev) => ({ ...prev, [columnId]: false }));
    }
  };

  const handleScroll = (columnId) => (event) => {
    const container = event.target;
    const scrollPosition = container.scrollTop + container.clientHeight;
    const scrollHeight = container.scrollHeight;

    if (scrollHeight - scrollPosition < 100) {
      loadMoreLeads(columnId);
    }
  };

  const onDragStart = () => {};

  const onDragEnd = async (result) => {
    const { source, destination } = result;

    if (!destination) return;

    if (source.droppableId === destination.droppableId && source.index === destination.index) {
      return;
    }

    const sourceColumnId = source.droppableId;
    const destColumnId = destination.droppableId;

    const sourceColumn = columns[sourceColumnId];
    const destColumn = columns[destColumnId];
    const sourceItems = Array.from(sourceColumn);
    const destItems = sourceColumnId === destColumnId ? sourceItems : Array.from(destColumn);

    const [movedItem] = sourceItems.splice(source.index, 1);

    const previousColumns = { ...columns };
    const previousStatus = movedItem.status;
    const previousWorkflowStatusId = movedItem.workflowStatusId;

    movedItem.status = destColumnId;
    movedItem.workflowStatusId = destColumnId;

    destItems.splice(destination.index, 0, movedItem);

    const updatedColumns = {
      ...columns,
      [sourceColumnId]: sourceItems,
      [destColumnId]: destItems,
    };

    setColumns(updatedColumns);

    if (sourceColumnId !== destColumnId) {
      try {
        await dispatch(
          bulkChangeLeadStatus({
            leadIds: [movedItem.id],
            workflowStatusId: destColumnId,
          })
        ).unwrap();

        setSelectedCards(new Set());
        if (onSelectionChange) {
          onSelectionChange([]);
        }
      } catch (error) {
        console.error('Failed to update lead status:', error);

        movedItem.status = previousStatus;
        movedItem.workflowStatusId = previousWorkflowStatusId;

        setColumns(previousColumns);
      }
    }
  };

  const handleSelectCard = (cardId) => {
    setSelectedCards((prev) => {
      const newSelected = new Set(prev);
      if (newSelected.has(cardId)) {
        newSelected.delete(cardId);
      } else {
        newSelected.add(cardId);
      }
      if (onSelectionChange) {
        onSelectionChange(Array.from(newSelected));
      }
      return newSelected;
    });
  };

  const handleSelectAll = (columnId) => {
    const columnCards = columns[columnId] || [];
    const columnCardIds = columnCards.map((card) => card.id);
    const allSelected = columnCardIds.every((id) => selectedCards.has(id));

    setSelectedCards((prev) => {
      const newSelected = new Set(prev);
      if (allSelected) {
        columnCardIds.forEach((id) => newSelected.delete(id));
      } else {
        columnCardIds.forEach((id) => newSelected.add(id));
      }
      if (onSelectionChange) {
        onSelectionChange(Array.from(newSelected));
      }
      return newSelected;
    });
  };

  const handlePriorityChange = async (cardId, newPriority) => {
    const previousColumns = { ...columns };
    let previousPriority = null;
    const allColumns = { ...columns };

    Object.keys(allColumns).forEach((columnId) => {
      const cardIndex = allColumns[columnId].findIndex((card) => card.id === cardId);
      if (cardIndex !== -1) {
        previousPriority = allColumns[columnId][cardIndex].priority;
        allColumns[columnId][cardIndex].priority = newPriority;
      }
    });

    setColumns(allColumns);
    try {
      await dispatch(
        updateLeadPriority({
          leadId: cardId,
          priority: newPriority,
        })
      ).unwrap();
    } catch (error) {
      console.error('Failed to update lead priority:', error);
      setColumns(previousColumns);
    }
  };

  const isColumnAllSelected = (columnId) => {
    const columnCards = columns[columnId] || [];
    if (columnCards.length === 0) return false;
    return columnCards.every((card) => selectedCards.has(card.id));
  };

  if (!columnDefinitions || columnDefinitions.length === 0) {
    return (
      <BoardContainer>
        <EmptyMessage>No workflow statuses configured</EmptyMessage>
      </BoardContainer>
    );
  }

  return (
    <DragDropContext onDragStart={onDragStart} onDragEnd={onDragEnd}>
      <BoardContainer>
        {columnDefinitions.map((column) => {
          const columnCards = columns[column.id] || [];
          const allSelected = isColumnAllSelected(column.id);

          return (
            <Column key={column.id}>
              <ColumnHeader $color={column.color}>
                <ColumnHeaderLeft>
                  <ColumnIcon $color={column.color}>
                    <IconStatus
                      src={column.outcomeType === 'initial' ? NewIcon : StatusIcon}
                    />
                  </ColumnIcon>
                  <ColumnTitle>{column.title}</ColumnTitle>
                </ColumnHeaderLeft>
                <ColumnCount $color={column.color}>
                  {columnPagination[column.id]?.total ?? columnCards.length}
                </ColumnCount>
                <CheckBoxBlock>
                  <ColumnCheckbox
                    type="checkbox"
                    checked={allSelected}
                    onChange={() => handleSelectAll(column.id)}
                    onClick={(e) => e.stopPropagation()}
                  />
                </CheckBoxBlock>
              </ColumnHeader>

              <Droppable droppableId={column.id}>
                {(provided, snapshot) => (
                  <CardsContainer
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                    $isDraggingOver={snapshot.isDraggingOver}
                    $color={column.color}
                    onScroll={handleScroll(column.id)}
                  >
                    {columnCards.map((lead, index) => (
                      <Draggable key={lead.id} draggableId={lead.id} index={index}>
                        {(provided, snapshot) => (
                          <div
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            {...provided.dragHandleProps}
                          >
                            <LeadCard
                              lead={lead}
                              isDragging={snapshot.isDragging}
                              isSelected={selectedCards.has(lead.id)}
                              onSelect={handleSelectCard}
                              onPriorityChange={handlePriorityChange}
                              statusColor={column.color}
                              canEditPriority={
                                userType === 'Super Admin' ||
                                userType === 'General Manager' ||
                                lead.assigneeUuid === userInfo?.uuid
                              }
                            />
                          </div>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                    {columnLoading[column.id] && (
                      <div style={{ padding: '10px', textAlign: 'center', color: '#666' }}>
                        Loading...
                      </div>
                    )}
                    {!columnHasMore[column.id] && columnCards.length > 0 && (
                      <div
                        style={{
                          padding: '10px',
                          textAlign: 'center',
                          color: '#999',
                          fontSize: '12px',
                        }}
                      >
                        No more items
                      </div>
                    )}
                  </CardsContainer>
                )}
              </Droppable>
            </Column>
          );
        })}
      </BoardContainer>
    </DragDropContext>
  );
};

export default KanbanBoard;
